import { Stream } from '@cloudflare/stream-react'
import type { ComponentProps } from 'react'
import { match } from 'ts-pattern'
import { CampaignMediaType } from 'src/services/apollo/generated/graphql'
import { cn } from 'src/utils/cx'
import HlsStream from '../Video/HlsStream'
import { VideoMedia } from './VideoMedia'

/**
 * Videos/Streams are looped by default
 *
 * @param props
 * @returns
 */
export function MediaStreamPlayer(
  props: {
    /** Reusing CampaignMediaType enum here */
    mediaType?:
      | typeof CampaignMediaType.Video
      | typeof CampaignMediaType.VideoHls
      | typeof CampaignMediaType.VideoStream
    /** ID for VideoStream, URL for others */
    mediaSpec: string

    /** Shared by all players */
    commonProps?: {
      onCanPlay?: () => void
      onLoadStart?: () => void
    }
    // Different props for specific players
    // Will override mutual props above
    videoMediaProps?: Omit<ComponentProps<typeof VideoMedia>, 'src'>
    hlsStreamProps?: Omit<ComponentProps<typeof HlsStream>, 'src'>
    streamProps?: Omit<ComponentProps<typeof Stream>, 'src'>
  } & Pick<
    StyleProps,
    // `Stream` doesn't support `style`
    'className'
  >,
) {
  return match(props.mediaType)
    .with(CampaignMediaType.Video, () => {
      const { className, ...rest } = props.videoMediaProps ?? {}
      return (
        <VideoMedia
          className={cn(props.className, className)}
          autoPlay
          src={props.mediaSpec}
          muted
          {...props.commonProps}
          {...rest}
        />
      )
    })
    .with(CampaignMediaType.VideoHls, () => {
      const { className, ...rest } = props.hlsStreamProps ?? {}
      return (
        <HlsStream
          className={cn(props.className, className)}
          src={props.mediaSpec}
          controls={false}
          autoPlay
          playsInline
          muted
          loop
          // crossOrigin must be consistent between fetch and cache reads;
          // without it, re-mounting the stream will cause the m3u8 to fail (ORB)
          crossOrigin="anonymous"
          {...props.commonProps}
          {...rest}
        />
      )
    })
    .with(CampaignMediaType.VideoStream, () => {
      const { className, ...rest } = props.streamProps ?? {}
      return (
        <Stream
          className={cn(props.className, className)}
          src={props.mediaSpec}
          controls={false}
          autoplay
          muted
          loop
          {...props.commonProps}
          {...rest}
        />
      )
    })
    .otherwise(() => null)
}
